import { NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import { Box, ToastAnimateFrom, ToastContent, ToastPosition, ToastType } from '@bechtel/gds';
import {
  ConstructionStatus,
  FeedbackCableInput,
  FeedbackCableResult,
  MetadataColumn,
  MetadataTableName,
  ProcessLength,
  Question,
  UiMessageKey,
  UiTextEnum,
} from '@bechtel/sr-types';
import { Formik } from 'formik';
import { find, get, includes, isNull, isUndefined, map, omit } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { array, number, object, string } from 'yup';
import { FormSkeleton, useAppUser, useLanguage, useTableMetadataContext } from '~/components';
import { FEEDBACK_CABLE, SEARCH_CABLE, SEARCH_CABLE_REELS } from '~/gql';
import { usePermissions, useTableMetadataQuery, useToast } from '~/hooks';
import { formatValues, getDescription } from '~/utilities';
import { RightPanelStyled } from '~/views/commodities/styled';
import { AdditionalInfo } from './additional-info';
import CableFeedbackForm from './form';
import { CableFeedbackFormikState } from './form/types';
import { cableFeedbackInitialValues, getFormikData, sanitizedData } from './utils';

const getYupSchema = (
  isAlternateLanguageUsed,
  getMultiLingualTextByKey,
  data: MetadataColumn[],
  permission: boolean = false,
  entityText: string = ''
) => {
  const constStatusDescription = getDescription(data, isAlternateLanguageUsed, 'constructionStatus');
  const tagDescription = getDescription(data, isAlternateLanguageUsed, 'tag');
  const actualLengthDescription = getDescription(data, isAlternateLanguageUsed, 'actualLength');
  const reelDescription = getDescription(data, isAlternateLanguageUsed, 'reelId');
  const cutLengthDescription = getDescription(data, isAlternateLanguageUsed, 'cutLength');
  const actualLengthMetadataColumn = find(data, (item) => item.memberName === 'actualLength');
  const cutLengthMetadataColumn = find(data, (item) => item.memberName === 'cutLength');
  const reelSchema = object().shape({
    tag: string().required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [reelDescription])),
  });

  const cableReelsSchema = object().shape({
    reel: reelSchema.required(),
    cutLength: number()
      .min(
        cutLengthMetadataColumn?.minValue ?? 0,
        getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
          cutLengthDescription,
          String(cutLengthMetadataColumn?.minValue),
          String(cutLengthMetadataColumn?.maxValue),
        ])
      )
      .max(
        cutLengthMetadataColumn?.maxValue ?? 99999.99,
        getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
          cutLengthDescription,
          String(cutLengthMetadataColumn?.minValue),
          String(cutLengthMetadataColumn?.maxValue),
        ])
      )
      .required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [cutLengthDescription])),
  });

  return {
    tag: string()
      .required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [tagDescription]))
      .test(
        'tag-check',
        getMultiLingualTextByKey(UiTextEnum.VALUE_IS_NOT_VALID, [getMultiLingualTextByKey(entityText)]),
        function () {
          const { tagInvalid } = this.parent;
          return tagInvalid === false;
        }
      ),
    constructionStatus:
      permission &&
      string().required(getMultiLingualTextByKey(UiTextEnum.VALUE_IS_NOT_VALID, [constStatusDescription])),
    actualLength: number().when('constructionStatus', {
      is: (value) => value > 0,
      then: (schema) =>
        schema
          .min(
            actualLengthMetadataColumn?.minValue ?? 0.01,
            getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
              actualLengthDescription,
              String(actualLengthMetadataColumn?.minValue),
              String(actualLengthMetadataColumn?.maxValue),
            ])
          )
          .max(
            actualLengthMetadataColumn?.maxValue ?? 99999.99,
            getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
              actualLengthDescription,
              String(actualLengthMetadataColumn?.minValue),
              String(actualLengthMetadataColumn?.maxValue),
            ])
          )
          .required(getMultiLingualTextByKey(UiTextEnum.VALUE_IS_NOT_VALID, [actualLengthDescription])),
      otherwise: (schema) => schema.notRequired(),
    }),
    ['cableReels' as string]: array().when('constructionStatus', {
      is: (value) => value === '0',
      then: () => array().of(cableReelsSchema),
      otherwise: (schema) => schema.notRequired(),
    }),
  };
};

const Cables = () => {
  const { showToast } = useToast();
  const [initialValues, setInitialValues] = useState<CableFeedbackFormikState>(cableFeedbackInitialValues);
  const [getTableMetaCableReels, { data: cableReels }] = useTableMetadataQuery();
  const tableMetadata = useTableMetadataContext();
  const cableReelTableMetadata = cableReels?.tableMetadata;
  const cableTableMetadata = tableMetadata;
  const appUser = useAppUser();
  const { canUpdateCableFeedback } = usePermissions();
  const [statusCheck, setStatusCheck] = useState<boolean>(true);
  const [errorKeysCheck, setErrorKeysCheck] = useState<boolean>(true);

  useEffect(() => {
    // Fetch metadata for CableReel
    getTableMetaCableReels({
      variables: {
        name: MetadataTableName.CABLE_REEL,
        projectId: appUser.userAccount?.defaultProject?.id,
      },
    });
  }, [appUser.userAccount?.defaultProject?.id, getTableMetaCableReels]);

  const { getMultiLingualTextByKey, isAlternateLanguageUsed } = useLanguage();
  const [searchCables, { data: searchCableData, loading, error, networkStatus }] = useLazyQuery(SEARCH_CABLE, {
    notifyOnNetworkStatusChange: true,
  });
  const [
    searchCableReels,
    { data: searchCableReelsData, loading: searchCableReelsLoading, networkStatus: searchCableReelsNetworkStatus },
  ] = useLazyQuery(SEARCH_CABLE_REELS, {
    notifyOnNetworkStatusChange: true,
  });
  const initialValueKeys = useRef(initialValues && Object?.keys(initialValues));
  const formikValuesRef = useRef(cableFeedbackInitialValues);
  const entityText = getMultiLingualTextByKey('Cable');
  const cableData = get(searchCableData, 'searchCables.items[0]');
  const cableReelData = get(searchCableReelsData, 'searchCableReels.items');

  const formikData = useMemo(() => {
    if (!cableData) return;
    const metadataColumnData = [
      ...(cableTableMetadata?.metadataColumns || []),
      ...(cableReelTableMetadata?.metadataColumns || []),
    ];
    if (cableReelData) {
      return getFormikData(
        { ...cableData, ['cableReel']: cableReelData },
        initialValueKeys.current,
        metadataColumnData,
        true
      );
    } else if (cableData) {
      return getFormikData(cableData, initialValueKeys.current, metadataColumnData);
    }
  }, [cableData, cableReelData, cableReelTableMetadata?.metadataColumns, cableTableMetadata?.metadataColumns]);

  //Setup initial values
  useEffect(() => {
    if (initialValues.constructionStatus !== ConstructionStatus.CABLE_CUT) {
      if (cableFeedbackInitialValues && cableTableMetadata?.metadataColumns) {
        const feedbackInitialData = formatValues(cableFeedbackInitialValues, cableTableMetadata.metadataColumns);
        if (feedbackInitialData && formikData) setInitialValues({ ...feedbackInitialData, ...formikData });
      }
    } else {
      setInitialValues({ ...cableFeedbackInitialValues, ...formikData });
    }
  }, [cableTableMetadata?.metadataColumns, formikData, initialValues.constructionStatus]);

  const [feedbackCable, { data: feedbackCableData, error: feedbackCableError, loading: feedbackCableLoading }] =
    useMutation<{ feedbackCable: FeedbackCableResult }, { feedbackCableInput: FeedbackCableInput }>(FEEDBACK_CABLE, {
      onCompleted: (data) => {
        if (feedbackCableError === undefined && data.feedbackCable.errors == null) {
          showToast(
            UiTextEnum.SAVED_SUCCESSFULLY_TOAST,
            [entityText, data?.feedbackCable?.cable?.tag ?? ''],
            ToastType.INFO
          );
        }
        setErrorKeysCheck(true);
      },
      update(cache, data) {
        if (!isNull(data?.data?.feedbackCable?.cable) && formikValuesRef.current.constructionStatus === '0') {
          setStatusCheck(false);
          cache.evict({ fieldName: 'searchCableReels' });
          cache.gc();
        }
      },
      onError: () => {
        showToast(UiTextEnum.ERROR_WHILE_UPDATING_TOAST, [entityText, ''], ToastType.WARNING);
      },
    });

  const validationSchema = object().shape(
    cableTableMetadata?.metadataColumns || cableReelTableMetadata?.metadataColumns
      ? getYupSchema(
          isAlternateLanguageUsed,
          getMultiLingualTextByKey,
          [...(cableTableMetadata?.metadataColumns || []), ...(cableReelTableMetadata?.metadataColumns || [])],
          canUpdateCableFeedback,
          entityText
        )
      : {}
  );

  const saveHandler = async (values) => {
    const id = cableData?.id;
    // Convert empty strings to null
    const sanitizedCableValues = sanitizedData(values, cableTableMetadata?.metadataColumns);
    let sanitizedReelValues;
    const updatedValues = { ...sanitizedCableValues, processLength: ProcessLength.CUT };
    const omitValues = omit(updatedValues, ['tag', 'tagInvalid', 'reelId']);

    if (omitValues && omitValues?.cableReels?.length > 0 && !includes(omitValues?.cableReels, '')) {
      const updatedCableReels = {
        cableReels: map(omitValues?.cableReels, (cableReel, index) => {
          const { reel, cutLength, assignedLength, reCut } = cableReel;
          const matchingData = find(cableReelData, { reel: { id: reel.id } });
          return {
            reelId: reel.id,
            reCut: reCut ? reCut : Question.NOT_ASKED,
            isDeleted: false,
            sequenceNumber: index + 1,
            cutLength: cutLength,
            assignedLength: assignedLength,
            originalLength: isUndefined(matchingData) ? 0 : matchingData.cutLength,
          };
        }),
      };
      sanitizedReelValues = map(updatedCableReels.cableReels, (reel) =>
        sanitizedData(reel, cableReelTableMetadata?.metadataColumns)
      );
    }
    const payload = {
      id,
      ...(values.constructionStatus === ConstructionStatus.CABLE_CUT && {
        ...omitValues,
        cableReels: sanitizedReelValues,
      }),
      ...(values.constructionStatus !== ConstructionStatus.CABLE_CUT && { ...omit(omitValues, ['cableReels']) }),
    };

    await feedbackCable({ variables: { feedbackCableInput: payload as FeedbackCableInput } });
  };

  const errorKeys: UiMessageKey[] =
    get(feedbackCableData, 'feedbackCable.infos') ?? get(feedbackCableData, 'feedbackCable.errors') ?? [];

  if (!cableTableMetadata && !cableReelTableMetadata) return <FormSkeleton columns={1} rows={5} />;

  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnChange={true}
        validateOnBlur={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          formikValuesRef.current = values;
          await saveHandler(values);
          setSubmitting(false);
        }}
      >
        <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
          <form style={{ width: '100%' }}>
            <CableFeedbackForm
              searchCables={searchCables}
              searchCableloading={loading || networkStatus === NetworkStatus.refetch}
              searchCableReelsLoading={
                searchCableReelsLoading || searchCableReelsNetworkStatus === NetworkStatus.refetch
              }
              searchCableReels={searchCableReels}
              cableData={cableData}
              cableReelTableMetadata={cableReelTableMetadata}
              cableTableMetadata={cableTableMetadata}
              feedbackCableLoading={feedbackCableLoading}
              initialValues={initialValues}
              setStatusCheck={setStatusCheck}
              statusCheck={statusCheck}
              errorKeys={errorKeys}
              saveHandler={saveHandler}
              formikValuesRef={formikValuesRef}
              errorKeysCheck={errorKeysCheck}
              setErrorKeysCheck={setErrorKeysCheck}
              setInitialValues={setInitialValues}
            />
          </form>
        </Box>
      </Formik>

      <ToastContent position={ToastPosition.MIDDLE} animateFrom={ToastAnimateFrom.BOTTOM} />
      <RightPanelStyled isCollapsed={true}>
        <AdditionalInfo
          data={cableData}
          loading={loading || networkStatus === NetworkStatus.refetch}
          error={error}
          cableReelTableMetadata={cableReelTableMetadata}
        />
      </RightPanelStyled>
    </>
  );
};

export default Cables;
